
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Ticket, Tag, RotateCcw } from "lucide-react";
import { useAvailableBookingCoupons, AvailableCoupon } from "@/hooks/useBookingCoupons";
import { useUserCouponCredits, useGetOrCreatePeriodCredits } from "@/hooks/useUserCouponCredits";
import { useCouponBookingHistory } from "@/hooks/useCouponBookingHistory";

interface CouponSelectorProps {
  serviceId?: string;
  providerId?: string;
  servicePrice: number;
  selectedCoupon?: AvailableCoupon;
  onCouponSelect: (coupon: AvailableCoupon | null) => void;
  selectedDate?: Date;
}

export const CouponSelector = ({ 
  serviceId, 
  providerId, 
  servicePrice, 
  selectedCoupon, 
  onCouponSelect,
  selectedDate 
}: CouponSelectorProps) => {
  const { data: availableCoupons, isLoading } = useAvailableBookingCoupons(serviceId, providerId);
  const getOrCreatePeriodCredits = useGetOrCreatePeriodCredits();

  const calculateDiscount = (coupon: AvailableCoupon) => {
    const { discount_type, discount_value } = coupon.coupon;
    
    // For credit-based coupons, the discount is the full service price
    if (discount_type === 'weekly_credits' || discount_type === 'monthly_credits') {
      return servicePrice;
    }
    
    if (discount_type === 'fixed') {
      return Math.min(discount_value, servicePrice);
    } else {
      return Math.min((servicePrice * discount_value) / 100, servicePrice);
    }
  };

  const CouponItem = ({ userCoupon }: { userCoupon: AvailableCoupon }) => {
    const coupon = userCoupon.coupon;
    const discount = calculateDiscount(userCoupon);
    const isSelected = selectedCoupon?.id === userCoupon.id;
    const isCreditBased = coupon.discount_type === 'weekly_credits' || coupon.discount_type === 'monthly_credits';
    
    console.log('CouponItem - userCoupon:', userCoupon);
    console.log('CouponItem - isCreditBased:', isCreditBased);
    console.log('CouponItem - credits_per_period:', coupon.credits_per_period);
    console.log('CouponItem - selectedDate:', selectedDate);
    
    // Pass the selected booking date to the hook so it fetches credits for the correct period
    const { data: creditInfo, refetch: refetchCredits } = useUserCouponCredits(
      isCreditBased ? userCoupon.id : undefined, 
      isCreditBased ? selectedDate : undefined
    );
    const { data: bookingHistory } = useCouponBookingHistory(userCoupon.id);

    console.log('CouponItem - creditInfo:', creditInfo);
    console.log('CouponItem - bookingHistory:', bookingHistory);

    const handleCouponClick = async () => {
      console.log('handleCouponClick - isCreditBased:', isCreditBased);
      console.log('handleCouponClick - selectedDate:', selectedDate);
      
      if (!isCreditBased) {
        onCouponSelect(isSelected ? null : userCoupon);
        return;
      }

      // For credit-based coupons, the hook already handles getting the correct period data
      // We just need to refetch to ensure we have the latest data
      try {
        await refetchCredits();
        onCouponSelect(isSelected ? null : userCoupon);
      } catch (error) {
        console.error('Error refetching coupon credits:', error);
        onCouponSelect(isSelected ? null : userCoupon);
      }
    };

    // For credit-based coupons, check credits based on the fetched credit info
    let hasAvailableCredits = true;
    let creditStatusMessage = "";
    
    if (isCreditBased) {
      const creditsPerPeriod = coupon.credits_per_period || 1;
      
      if (creditInfo) {
        hasAvailableCredits = creditInfo.credits_used < creditInfo.credits_available;
        const remaining = creditInfo.credits_available - creditInfo.credits_used;
        creditStatusMessage = hasAvailableCredits ? 
          `${remaining} credit${remaining !== 1 ? 's' : ''} remaining this period` :
          `All ${creditInfo.credits_available} credit${creditInfo.credits_available !== 1 ? 's' : ''} used this period`;
      } else {
        // No credit info yet - assume credits are available
        hasAvailableCredits = true;
        creditStatusMessage = `${creditsPerPeriod} credit${creditsPerPeriod !== 1 ? 's' : ''} available`;
      }
      
      console.log('CouponItem - final hasAvailableCredits:', hasAvailableCredits);
      console.log('CouponItem - creditStatusMessage:', creditStatusMessage);
    }

    return (
      <div
        className={`p-3 sm:p-4 border rounded-lg cursor-pointer transition-colors ${
          isSelected 
            ? 'border-blue-500 bg-blue-50' 
            : hasAvailableCredits
            ? 'border-gray-200 hover:border-gray-300'
            : 'border-gray-100 bg-gray-50 opacity-60'
        }`}
        onClick={() => hasAvailableCredits && handleCouponClick()}
      >
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-between sm:items-start">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Badge variant="secondary" className="text-xs shrink-0">
                {isCreditBased ? (
                  <RotateCcw className="h-3 w-3 mr-1" />
                ) : (
                  <Tag className="h-3 w-3 mr-1" />
                )}
                {coupon.code}
              </Badge>
              <span className="font-medium text-sm truncate">{coupon.name}</span>
              {isCreditBased && (
                <Badge variant="outline" className="text-xs bg-blue-100 text-blue-800 shrink-0">
                  {coupon.discount_type === 'weekly_credits' ? 'Weekly' : 'Monthly'}
                </Badge>
              )}
            </div>
            {coupon.description && (
              <p className="text-xs text-gray-600 mb-2 line-clamp-2">{coupon.description}</p>
            )}
            <div className="text-sm">
              {isCreditBased ? (
                <div>
                  <span className="text-blue-600 font-medium">
                    Free Session
                  </span>
                  {creditStatusMessage && (
                    <div className="text-xs text-gray-500 mt-1">
                      {creditStatusMessage}
                    </div>
                  )}
                  {bookingHistory && bookingHistory.length > 0 && (
                    <div className="text-xs text-gray-400 mt-1">
                      Total bookings: {bookingHistory.length}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <span className="text-green-600 font-medium">
                    Save £{discount.toFixed(2)}
                  </span>
                  {coupon.discount_type === 'percentage' && (
                    <span className="text-gray-500 ml-1">
                      ({coupon.discount_value}% off)
                    </span>
                  )}
                  {bookingHistory && bookingHistory.length > 0 && (
                    <div className="text-xs text-gray-400 mt-1">
                      Used {bookingHistory.length} time{bookingHistory.length !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="sm:ml-4 sm:shrink-0">
            <Button
              variant={isSelected ? "default" : "outline"}
              size="sm"
              disabled={!hasAvailableCredits}
              className="w-full sm:w-auto min-w-[80px]"
            >
              {!hasAvailableCredits ? "No Credits" : isSelected ? "Applied" : "Apply"}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Ticket className="h-5 w-5" />
            <span>Available Coupons</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!availableCoupons || availableCoupons.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Ticket className="h-5 w-5" />
          <span>Available Coupons</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {availableCoupons.map((userCoupon) => (
          <CouponItem key={userCoupon.id} userCoupon={userCoupon} />
        ))}
      </CardContent>
    </Card>
  );
};

export default CouponSelector;
